import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import InfoTip from "../../shared/InfoTip";

/**
 * LineChartWithZero — forecast_reliability evidence component.
 *
 * Boxed chart (like ExpectedVsActual / GroupedBarChart market_tolerance):
 * card with a title + InfoTip header, a left-side FYI sidebar holding the
 * legend and volatility stats, and the drift line chart on the right with
 * a zero reference. A centered caption sits below the graph.
 *
 * Spec references: §7 (forecast_reliability).
 */

const fmtNum = (v) => Number(v ?? 0).toLocaleString();

const CHART_TITLE = "Forecast Drift by Lead Time";
const INFO_TIP_TEXT =
  "Average forecast error (°F) at each lead time. Negative = forecasts run cooler than reality. The zero line marks no bias.";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-xs">
      <p className="font-semibold text-gray-800 dark:text-gray-200">
        {d.days_ahead} day{d.days_ahead === 1 ? "" : "s"} ahead
      </p>
      <p className="text-gray-600 dark:text-gray-400">
        Avg error: {d.avg_error_f > 0 ? "+" : ""}{d.avg_error_f}°F
      </p>
      <p className="text-gray-400 dark:text-gray-500">
        {fmtNum(d.samples)} samples
      </p>
    </div>
  );
};

const LineChartWithZero = ({ panel }) => {
  const f = panel.facts || {};
  const drift = f.drift_by_lead_time || [];
  const vol = f.volatility || {};

  if (drift.length === 0) return null;

  return (
    <div>
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      {/* ─── Card header: title + InfoTip (left), window/sessions (right) ── */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <span>{CHART_TITLE}</span>
          <InfoTip text={INFO_TIP_TEXT} />
        </h3>
        {f.window_days && f.sessions != null && (
          <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Last {f.window_days} days · {fmtNum(f.sessions)} visits
          </p>
        )}
      </div>

      {/* ─── Body: FYI sidebar (left) + chart (right) ──────────────────── */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* FYI sidebar — legend + volatility stats */}
        <aside className="lg:w-44 flex-shrink-0 space-y-3 lg:border-r lg:border-gray-200 dark:lg:border-gray-800 lg:pr-4">
          {/* Legend */}
          <div className="space-y-2 text-[11px] text-gray-500 dark:text-gray-400">
            <p className="font-semibold text-gray-600 dark:text-gray-300">Legend</p>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-4 h-0.5 rounded-sm bg-blue-500" />
              Avg forecast error (°F)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-4 h-0 border-t-2 border-dashed border-gray-400" />
              0°F (no bias)
            </span>
          </div>

          {/* Volatility stats */}
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
              Volatility
            </p>
            <div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Avg swing
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {vol.avg_swing_f}°F
              </p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Max swing
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {vol.max_swing_f}°F
              </p>
            </div>
            {vol.most_volatile_date && (
              <div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  Most volatile
                </p>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {vol.most_volatile_date}
                </p>
              </div>
            )}
          </div>
        </aside>

        {/* Chart */}
        <div className="flex-1 min-w-0 min-h-[240px]">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={drift} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
              <XAxis
                dataKey="days_ahead"
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                axisLine={{ stroke: "#E5E7EB" }}
                tickLine={false}
                label={{ value: "Days ahead", position: "insideBottom", offset: -2, fontSize: 10, fill: "#9CA3AF" }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}°`}
                width={40}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine
                y={0}
                stroke="#9CA3AF"
                strokeDasharray="3 3"
                label={{ value: "0°F (no bias)", position: "right", fontSize: 10, fill: "#9CA3AF" }}
              />
              <Line
                type="monotone"
                dataKey="avg_error_f"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={{ r: 3, fill: "#3B82F6" }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Caption — centered below the graph */}
      <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center italic mt-3 leading-snug">
        In the final week before arrival. Negative = forecasts run cooler than reality.
      </p>

    </div>

      {/* Insight — outside the graph box (panel.sentence suppressed at page level) */}
      {panel.sentence && (
        <div className="mt-3 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <p className="text-xs text-blue-800 dark:text-blue-300 leading-snug">
            <span className="font-semibold">Insight:</span> {panel.sentence}
          </p>
        </div>
      )}
    </div>
  );
};

export default LineChartWithZero;